import { useEffect, useId, useMemo, useState } from 'react';
import {
  Alert,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link as MuiLink,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ALWAYS_SHRINK } from '../../../theme/shellMetrics';

import { processesApi } from '../../../api';
import type { MeColleague } from '../../../api/me';
import type { ProcessFlowNodeType, ProcessRole } from '../../../api/processes';
import { useProcessEntitlements } from '../../../hooks/useProcessEntitlements';
import { approvalTargetHours } from '../approvalTarget';
import {
  ColleaguePickerMulti,
  ColleaguePickerSingle,
} from '../../../components/worker/ColleaguePicker';
import {
  processFormFieldKind,
  type ProcessFormFieldKind,
  type ProcessFormFieldLike,
  type ProcessFormSchemaLike,
} from '../../WorkerPortal/processFormContract';

type ScalarFieldKind = Exclude<
  ProcessFormFieldKind,
  'location' | 'attachment' | 'unsupported'
>;
type ConditionOperator = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq' | 'in' | 'not_in';

const ALL_OPERATORS: readonly ConditionOperator[] = [
  'gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'in', 'not_in',
];
const EQUALITY_OPERATORS: readonly ConditionOperator[] = ['eq', 'neq', 'in', 'not_in'];
const LIST_OPERATORS = new Set<ConditionOperator>(['in', 'not_in']);

const OPERATOR_KEYS: Record<ConditionOperator, string> = {
  gt: 'greaterThan',
  gte: 'greaterThanOrEqual',
  lt: 'lessThan',
  lte: 'lessThanOrEqual',
  eq: 'equals',
  neq: 'notEquals',
  in: 'inList',
  not_in: 'notInList',
};

function isScalarFieldKind(kind: ProcessFormFieldKind): kind is ScalarFieldKind {
  return !['location', 'attachment', 'unsupported'].includes(kind);
}

function operatorsFor(kind: ScalarFieldKind | undefined): readonly ConditionOperator[] {
  return kind === 'number' || kind === 'currency' || kind === undefined
    ? ALL_OPERATORS
    : EQUALITY_OPERATORS;
}

/**
 * The scalar fields a condition can read, each with a label that is UNIQUE in the list.
 *
 * Two fields both called "Department" appeared identically here, and picking the wrong one
 * mis-routes the approval with no signal — the id is the identity, the label is the only
 * thing shown. `validate_form_schema` now REFUSES duplicate labels at publish, which is
 * the real fix; this still disambiguates because the condition editor runs on an
 * UNPUBLISHED draft, so an admin can create the collision and choose from it before
 * publish ever sees the form.
 *
 * The suffix is the field ID, and this is a legitimate place for one: the audience is the
 * admin wiring a routing rule, and the id is exactly what they need to know
 * (svdy-rules §14's technical-audience exception). It is added ONLY to labels that repeat,
 * so an unambiguous form looks unchanged.
 */
function scalarFields(schema: ProcessFormSchemaLike | null | undefined): Array<{
  id: string;
  label: string;
  kind: ScalarFieldKind;
}> {
  const rows = (schema?.fields ?? []).flatMap((field: ProcessFormFieldLike) => {
    const id = typeof field.id === 'string' ? field.id.trim() : '';
    const kind = processFormFieldKind(field.type);
    if (!id || !isScalarFieldKind(kind)) return [];
    return [{
      id,
      label: field.properties?.label || field.label || id,
      kind,
    }];
  });
  // Same normalisation the backend uses, so the two agree on what "the same name" means.
  const normalise = (label: string) => label.split(/\s+/).filter(Boolean).join(' ').toLowerCase();
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const key = normalise(row.label);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return rows.map((row) => (
    (counts.get(normalise(row.label)) ?? 0) > 1
      ? { ...row, label: `${row.label} (${row.id})` }
      : row
  ));
}

function valueText(value: unknown): string {
  return Array.isArray(value) ? value.map(String).join(', ') : String(value ?? '');
}

function coerceValue(text: string, operator: ConditionOperator, kind: ScalarFieldKind | undefined): unknown {
  const values = LIST_OPERATORS.has(operator)
    ? text.split(',').map((item) => item.trim()).filter(Boolean)
    : [text];
  const coerced = values.map((item) => {
    if (kind !== 'number' && kind !== 'currency') return item;
    const numeric = Number(item);
    return item.trim() !== '' && Number.isFinite(numeric) ? numeric : item;
  });
  return LIST_OPERATORS.has(operator) ? coerced : coerced[0] ?? '';
}

function firstScalar(value: unknown): string {
  if (!Array.isArray(value)) return String(value ?? '');
  return value.length > 0 ? String(value[0]) : '';
}

type Resolver = Record<string, unknown>;

function colleagueFrom(value: unknown, fallbackName?: unknown): MeColleague | null {
  if (typeof value !== 'string' || !value) return null;
  return {
    worker_id: value,
    name: typeof fallbackName === 'string' && fallbackName ? fallbackName : value,
    position: null,
    department: null,
    avatar_url: null,
    email: null,
    phone: null,
  };
}

function resolverKind(resolver: Resolver | undefined, legacyRole?: unknown) {
  if (resolver?.kind === 'specific_worker' || (typeof legacyRole === 'string' && legacyRole.startsWith('wrk_'))) return 'specific_worker';
  if (resolver?.kind === 'role' || (typeof legacyRole === 'string' && legacyRole !== 'manager')) return 'role';
  // Escalation targets carry `approver_manager` — the manager of whoever is
  // holding the overdue task, as opposed to `manager_chain`'s requester walk.
  if (resolver?.kind === 'approver_manager') return 'approver_manager';
  return 'manager_chain';
}

function ResolverFields({
  value,
  label,
  roles,
  onChange,
  // The escalation target uses the SAME three strategies as an approver, with
  // one difference: its manager option walks from the current APPROVER, not
  // from the requester. Passing the kind in keeps one component instead of a
  // near-copy that would drift (svdy-rules §10.5).
  managerKind = 'manager_chain',
}: {
  value: Resolver;
  label: string;
  roles: ProcessRole[];
  onChange: (next: Resolver) => void;
  managerKind?: 'manager_chain' | 'approver_manager';
}) {
  const { t } = useTranslation();
  const kind = resolverKind(value);
  const selectedWorker = colleagueFrom(value.worker_id, value.worker_label);
  // Without a labelId the Select renders a combobox with NO accessible name —
  // a screen reader announces "combobox" and nothing else. This has been true of
  // the approver picker since it shipped; the condition-field Select further
  // down this file was already wired correctly, which is the two-conventions
  // tell (svdy-rules §10.5b). `useId` because this component renders up to three
  // times in one panel (approver, fallback, escalation target).
  const labelId = useId();

  return (
    <Stack spacing={1.5}>
      <FormControl fullWidth size="small">
        <InputLabel id={labelId}>{label}</InputLabel>
        <Select
          labelId={labelId}
          label={label}
          value={kind}
          onChange={(event) => {
            const nextKind = event.target.value;
            const fallback = value.fallback;
            const next = nextKind === managerKind
              ? (managerKind === 'manager_chain'
                ? { kind: 'manager_chain', level: 1 }
                : { kind: 'approver_manager' })
              : nextKind === 'role'
                ? { kind: 'role', role_code: roles[0]?.name ?? '' }
                : { kind: 'specific_worker', worker_id: '' };
            onChange(fallback ? { ...next, fallback } : next);
          }}
        >
          <MenuItem value={managerKind}>
            {managerKind === 'manager_chain'
              ? t('processes:flowEditor.properties.directManager')
              : t('processes:processDesigner.overdue.approverManager')}
          </MenuItem>
          <MenuItem value="role">{t('processes:flowEditor.properties.byRole')}</MenuItem>
          <MenuItem value="specific_worker">{t('processes:flowEditor.properties.specificWorker')}</MenuItem>
        </Select>
      </FormControl>
      {kind === 'manager_chain' && (
        <TextField
          {...ALWAYS_SHRINK}
          label={t('processes:processDesigner.executionRules.managerLevel')}
          type="number"
          size="small"
          value={Number(value.level ?? 1)}
          onChange={(event) => onChange({ ...value, kind, level: Math.max(1, Number(event.target.value) || 1) })}
          inputProps={{ min: 1, step: 1 }}
          fullWidth
        />
      )}
      {kind === 'role' && (
        <FormControl fullWidth size="small">
          <InputLabel>{t('processes:flowEditor.properties.role')}</InputLabel>
          <Select
            label={t('processes:flowEditor.properties.role')}
            value={String(value.role_code ?? '')}
            onChange={(event) => onChange({ ...value, kind, role_code: event.target.value })}
          >
            {roles.map((role) => <MenuItem key={role.id} value={role.name}>{role.name}</MenuItem>)}
          </Select>
          {/* An EMPTY dropdown with no explanation was the whole of this state. It is not a
              blocker — a new approval node defaults to `manager_chain`, so nobody is forced
              through here — but an admin who deliberately picks "a process role" and finds
              nothing to pick has no idea what to do next, and the page they need is two
              modules away. Jotform's equivalent avoids this by pre-filling the approver with
              your own address; the principle is the same: never leave a required selector
              with nothing in it and nothing to say. */}
          {roles.length === 0 && (
            <FormHelperText>
              <MuiLink component={RouterLink} to="/processes/roles">
                {t('processes:flowEditor.properties.noRolesYet')}
              </MuiLink>
            </FormHelperText>
          )}
        </FormControl>
      )}
      {kind === 'specific_worker' && (
        <ColleaguePickerSingle
          label={t('processes:flowEditor.properties.specificWorker')}
          value={selectedWorker}
          onChange={(worker) => onChange({
            ...value,
            kind,
            worker_id: worker?.worker_id ?? '',
            worker_label: worker?.name ?? '',
          })}
        />
      )}
    </Stack>
  );
}

/**
 * What happens when this approval passes its due date.
 *
 * Lives on the node, beside the due date it depends on — not on a Policies page.
 * `sla_hours` above is what the engine turns into `ProcessTask.due_at`, and
 * `app/tasks/process_sla_cron.py` is the consumer. Anything not consumed there
 * must not appear here.
 *
 * The two controls carry different risk, so they carry different defaults: a
 * reminder only adds a notification, while reassignment changes who can approve
 * and therefore what the dashboard queue shows.
 */
function OverdueFields({
  config,
  roles,
  hasDueDate,
  onChange,
}: {
  config: Record<string, unknown>;
  roles: ProcessRole[];
  hasDueDate: boolean;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  // Automatic reassignment is Professional+ (svdy-platform alembic 117), enforced at publish. The
  // switch is disabled below the plan rather than left to fail on publish — see
  // `useProcessEntitlements` for why it fails OPEN while the profile is in flight.
  const { escalation: mayEscalate, loading: entitlementLoading } = useProcessEntitlements();
  const escalationLocked = !entitlementLoading && !mayEscalate;
  const overdue = (config.overdue ?? {}) as {
    remind?: boolean;
    escalate?: { after_hours?: number; to?: Resolver };
  };
  const remind = overdue.remind === true;
  const escalate = overdue.escalate;

  // No due date means no `due_at`, so nothing can ever be overdue — the switches
  // must not WRITE anything. But hiding the whole group taught the admin the
  // capability did not exist: the owner asked "没有高级设置配置的步骤吗？？？？？"
  // while looking straight at the panel that holds it. A control wired to nothing
  // is a lie; a control nobody can find is a feature that does not ship
  // (svdy-ui §12.12). So it renders, disabled, saying what to do first.

  const write = (next: { remind?: boolean; escalate?: { after_hours?: number; to?: Resolver } }) => {
    const rest = Object.fromEntries(
      Object.entries(config).filter(([key]) => key !== 'overdue'),
    );
    const block: Record<string, unknown> = {};
    if (next.remind) block.remind = true;
    if (next.escalate) block.escalate = next.escalate;
    // An empty `overdue: {}` would be a stored setting with no behaviour, which
    // is the class of thing this slice exists to remove.
    onChange(Object.keys(block).length ? { ...rest, overdue: block } : rest);
  };

  return (
    <Stack spacing={1.5} sx={{ pt: 0.5 }}>
      <Typography variant="caption" color="text.secondary">
        {t('processes:processDesigner.overdue.groupLabel')}
      </Typography>
      {!hasDueDate && (
        <Typography variant="caption" color="text.secondary">
          {t('processes:processDesigner.overdue.needsDueDate')}
        </Typography>
      )}
      <FormControlLabel
        disabled={!hasDueDate}
        control={<Switch
          checked={remind}
          onChange={(_, checked) => write({ remind: checked, escalate })}
        />}
        label={t('processes:processDesigner.overdue.remind')}
      />
      <FormControlLabel
        disabled={!hasDueDate || escalationLocked}
        control={<Switch
          checked={Boolean(escalate)}
          onChange={(_, checked) => write({
            remind,
            escalate: checked
              // 24h after the due date, to the approver's own manager: the
              // smallest choice a shop owner would make by hand, and the one
              // Jotform's Escalation defaults to in shape (a wait, then someone
              // else). Never `manager_chain` — that is the REQUESTER's manager,
              // who on this product's commonest flow is the person already late.
              ? { after_hours: 24, to: { kind: 'approver_manager' } }
              : undefined,
          })}
        />}
        label={t('processes:processDesigner.overdue.reassign')}
      />
      {escalationLocked && (
        // A disabled control with no reason is the same dead end as a red toast: the admin sees
        // that they cannot, and not what would let them. Named tier, not "upgrade your plan".
        <Typography variant="caption" color="text.secondary">
          {t('processes:processDesigner.overdue.reassignNeedsPlan')}
        </Typography>
      )}      {escalate && (
        <>
          <TextField
          {...ALWAYS_SHRINK}
            label={t('processes:processDesigner.overdue.waitHours')}
            type="number"
            size="small"
            value={Number(escalate.after_hours ?? 24)}
            onChange={(event) => write({
              remind,
              escalate: {
                ...escalate,
                after_hours: Math.max(0, Number(event.target.value) || 0),
              },
            })}
            helperText={t('processes:processDesigner.overdue.waitHelper')}
            inputProps={{ min: 0, step: 1 }}
            fullWidth
          />
          <ResolverFields
            value={(escalate.to ?? { kind: 'approver_manager' }) as Resolver}
            label={t('processes:processDesigner.overdue.reassignTo')}
            roles={roles}
            managerKind="approver_manager"
            onChange={(next) => write({ remind, escalate: { ...escalate, to: next } })}
          />
        </>
      )}
    </Stack>
  );
}

export default function ExecutionRuleFields({
  nodeType,  config,
  formSchema,
  onChange,
  readOnly = false,
}: {
  nodeType: ProcessFlowNodeType;
  config: Record<string, unknown>;
  formSchema?: ProcessFormSchemaLike | null;
  onChange: (next: Record<string, unknown>) => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const [roles, setRoles] = useState<ProcessRole[]>([]);

  useEffect(() => {
    let cancelled = false;
    processesApi.processRolesApi.list()
      .then((rows) => { if (!cancelled) setRoles(rows.filter((role) => role.is_active)); })
      .catch(() => { if (!cancelled) setRoles([]); });
    return () => { cancelled = true; };
  }, []);

  const resolver = (config.resolver as Resolver | undefined) ?? (
    typeof config.approver_role === 'string'
      ? config.approver_role === 'manager'
        ? { kind: 'manager_chain', level: Number(config.manager_level ?? 1) }
        : config.approver_role.startsWith('wrk_')
          ? { kind: 'specific_worker', worker_id: config.approver_role }
          : { kind: 'role', role_code: config.approver_role }
      : { kind: 'manager_chain', level: 1 }
  );
  const condition = (config.condition as Record<string, unknown> | undefined) ?? {};
  const conditionFields = useMemo(() => scalarFields(formSchema), [formSchema]);
  const currentFieldId = typeof condition.field === 'string' ? condition.field : '';
  const selectedConditionField = conditionFields.find((field) => field.id === currentFieldId);
  const legacyConditionField = Boolean(currentFieldId && !selectedConditionField);
  const currentOperator = ALL_OPERATORS.includes(condition.op as ConditionOperator)
    ? condition.op as ConditionOperator
    : 'eq';
  const allowedOperators = operatorsFor(selectedConditionField?.kind);
  const displayedOperators = allowedOperators.includes(currentOperator)
    ? allowedOperators
    : [currentOperator, ...allowedOperators];
  const canonicalConditionValueText = valueText(condition.value);
  const [listValueDraft, setListValueDraft] = useState(canonicalConditionValueText);
  useEffect(() => {
    setListValueDraft(canonicalConditionValueText);
  }, [currentFieldId, currentOperator, canonicalConditionValueText]);
  const ccRecipients = useMemo(() => (
    Array.isArray(config.recipients)
      ? config.recipients.map((item) => {
          if (typeof item === 'string') return colleagueFrom(item);
          if (item && typeof item === 'object') {
            const recipient = item as Record<string, unknown>;
            return colleagueFrom(recipient.worker_id, recipient.name);
          }
          return null;
        }).filter((item): item is MeColleague => item !== null)
      : []
  ), [config.recipients]);
  const configuredTargetHours = approvalTargetHours(config);

  if (nodeType === 'approval') {
    const fallback = resolver.fallback as Resolver | undefined;
    return (
      <Stack component="fieldset" disabled={readOnly} spacing={2} sx={{ p: 0, m: 0, border: 0, minInlineSize: 0 }}>
        <ResolverFields
          value={resolver}
          label={t('processes:flowEditor.properties.approverAssignment')}
          roles={roles}
          onChange={(next) => onChange({ ...config, resolver: next })}
        />
        <TextField
          {...ALWAYS_SHRINK}
          label={t('processes:flowEditor.properties.dueDateDays')}
          type="number"
          size="small"
          value={configuredTargetHours == null
            ? ''
            : Number((configuredTargetHours / 24).toFixed(2))}
          onChange={(event) => {
            const next = { ...config };
            delete next.due_in_hours;
            if (event.target.value === '') {
              delete next.sla_hours;
            } else {
              next.sla_hours = Math.max(1, Number(event.target.value) * 24);
            }
            onChange(next);
          }}
          helperText={t('processes:flowEditor.properties.dueDateHelper')}
          fullWidth
          inputProps={{ min: 0.04, step: 0.25 }}
        />
        <FormControlLabel
          control={<Switch
            checked={Boolean(fallback)}
            onChange={(_, checked) => onChange({
              ...config,
              resolver: checked
                ? { ...resolver, fallback: { kind: 'role', role_code: roles[0]?.name ?? '' } }
                : Object.fromEntries(Object.entries(resolver).filter(([key]) => key !== 'fallback')),
            })}
          />}
          label={t('processes:processDesigner.executionRules.useFallback')}
        />
        {/* The "when" moved OUT of the switch label. At 63 characters it wrapped to
            two lines, and a 2-line label behind a control pushes both lines to the
            control's right edge, giving the card two left origins — the shape
            `layoutAlignment.test.ts` already forbade, in a directory its scope did
            not reach (owner screenshot 2026-08-23 19:46). As a caption it keeps the
            meaning and sits on the panel's own axis. */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          {t('processes:processDesigner.executionRules.useFallbackHint')}
        </Typography>
        {fallback && (
          <ResolverFields
            value={fallback}
            label={t('processes:processDesigner.executionRules.fallbackApprover')}
            roles={roles}
            onChange={(next) => onChange({ ...config, resolver: { ...resolver, fallback: next } })}
          />
        )}
        <OverdueFields
          config={config}
          roles={roles}
          hasDueDate={configuredTargetHours != null && configuredTargetHours > 0}
          onChange={onChange}
        />
      </Stack>
    );
  }

  if (nodeType === 'condition') {
    return (
      <Stack component="fieldset" disabled={readOnly} spacing={1.5} sx={{ p: 0, m: 0, border: 0, minInlineSize: 0 }}>
        {legacyConditionField && (
          <Alert severity="warning">
            {t('processes:flowEditor.properties.legacyConditionField', { field: currentFieldId })}
          </Alert>
        )}
        <FormControl fullWidth size="small">
          <InputLabel id="execution-condition-field-label">{t('processes:flowEditor.properties.conditionField')}</InputLabel>
          <Select
            labelId="execution-condition-field-label"
            label={t('processes:flowEditor.properties.conditionField')}
            value={currentFieldId}
            onChange={(event) => {
              const nextField = conditionFields.find((field) => field.id === event.target.value);
              const nextAllowed = operatorsFor(nextField?.kind);
              const nextOperator = nextAllowed.includes(currentOperator) ? currentOperator : 'eq';
              const nextText = LIST_OPERATORS.has(nextOperator)
                ? valueText(condition.value)
                : firstScalar(condition.value);
              onChange({
                ...config,
                condition: {
                  ...condition,
                  field: event.target.value,
                  op: nextOperator,
                  value: coerceValue(nextText, nextOperator, nextField?.kind),
                },
              });
            }}
          >
            {legacyConditionField && (
              <MenuItem value={currentFieldId}>
                {currentFieldId} ({t('processes:flowEditor.properties.legacyValue')})
              </MenuItem>
            )}
            {conditionFields.map((field) => (
              <MenuItem key={field.id} value={field.id}>{field.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel id="execution-condition-operator-label">{t('processes:flowEditor.properties.operator')}</InputLabel>
          <Select
            labelId="execution-condition-operator-label"
            label={t('processes:flowEditor.properties.operator')}
            value={currentOperator}
            onChange={(event) => {
              const nextOperator = event.target.value as ConditionOperator;
              const nextValue = LIST_OPERATORS.has(nextOperator)
                ? coerceValue(valueText(condition.value), nextOperator, selectedConditionField?.kind)
                : coerceValue(firstScalar(condition.value), nextOperator, selectedConditionField?.kind);
              onChange({ ...config, condition: { ...condition, op: nextOperator, value: nextValue } });
            }}
          >
            {displayedOperators.map((operator) => (
              <MenuItem key={operator} value={operator} disabled={!allowedOperators.includes(operator)}>
                {t(`processes:flowEditor.properties.${OPERATOR_KEYS[operator]}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          {...ALWAYS_SHRINK}
          label={t('processes:flowEditor.properties.value')}
          size="small"
          value={LIST_OPERATORS.has(currentOperator) ? listValueDraft : canonicalConditionValueText}
          onChange={(event) => {
            if (LIST_OPERATORS.has(currentOperator)) {
              setListValueDraft(event.target.value);
              return;
            }
            onChange({
              ...config,
              condition: {
                ...condition,
                value: coerceValue(event.target.value, currentOperator, selectedConditionField?.kind),
              },
            });
          }}
          onBlur={() => {
            if (!LIST_OPERATORS.has(currentOperator)) return;
            onChange({
              ...config,
              condition: {
                ...condition,
                value: coerceValue(listValueDraft, currentOperator, selectedConditionField?.kind),
              },
            });
          }}
          placeholder={t('processes:flowEditor.properties.valuePlaceholder')}
          helperText={t(LIST_OPERATORS.has(currentOperator)
            ? 'processes:flowEditor.properties.listValueHelper'
            : 'processes:flowEditor.properties.valueHelper')}
          fullWidth
        />
      </Stack>
    );
  }

  if (nodeType === 'notification') {
    return (
      <TextField
          {...ALWAYS_SHRINK}
        disabled={readOnly}
        label={t('processes:flowEditor.properties.messageTemplate')}
        multiline
        rows={3}
        size="small"
        value={String(config.message_template ?? '')}
        onChange={(event) => onChange({ ...config, message_template: event.target.value })}
        placeholder={t('processes:flowEditor.properties.messageTemplatePlaceholder')}
        fullWidth
      />
    );
  }

  if (nodeType === 'cc') {
    return (
      <Stack component="fieldset" disabled={readOnly} spacing={1.5} sx={{ p: 0, m: 0, border: 0, minInlineSize: 0 }}>
        <ColleaguePickerMulti
          label={t('processes:flowEditor.properties.ccRecipients')}
          value={ccRecipients}
          onChange={(workers) => onChange({
            ...config,
            recipients: workers.map((worker) => ({ worker_id: worker.worker_id, name: worker.name })),
          })}
        />
        <TextField
          {...ALWAYS_SHRINK}
          label={t('processes:flowEditor.properties.ccMessageTemplate')}
          multiline
          rows={3}
          size="small"
          value={String(config.message_template ?? '')}
          onChange={(event) => onChange({ ...config, message_template: event.target.value })}
          placeholder={t('processes:flowEditor.properties.ccMessagePlaceholder')}
          fullWidth
        />
      </Stack>
    );
  }

  return null;
}
